import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import './controls.css';

export { ColorPicker } from './colorPicker.tsx';

export function Readout({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): JSX.Element {
  return (
    <p class="ui-readout" title={hint}>
      <span class="ui-readout-label">{label}</span>
      <span class="ui-readout-value">{value}</span>
    </p>
  );
}

export function Toggle({
  label,
  value,
  onChange,
  disabled = false,
  hint,
}: {
  label: string;
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  hint?: string;
}): JSX.Element {
  return (
    <div class="ui-control" title={hint}>
      <span class="ui-control-label">{label}</span>
      <button
        type="button"
        class="ui-button ui-toggle"
        aria-pressed={value ? 'true' : 'false'}
        aria-label={label}
        aria-description={hint}
        disabled={disabled}
        onClick={() => onChange(!value)}
      >
        {value ? 'on' : 'off'}
      </button>
    </div>
  );
}

export function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T) => void;
  hint?: string;
}): JSX.Element {
  return (
    <div class="ui-control ui-control-wide" title={hint}>
      <span class="ui-control-label">{label}</span>
      <select
        class="ui-select"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Left uncontrolled and synced through a ref so a re-render triggered by an unrelated panel
 * cannot overwrite a value the user is still typing. Partial keystrokes (a lone `4` on the way
 * to `4096`) stay local until they are in range or the field blurs; the document clamps on blur.
 */
export function NumberField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (next: number) => void;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const element = input.current;

    if (element && document.activeElement !== element) {
      element.value = String(value);
    }
  }, [value]);

  const commit = (raw: string): void => {
    const next = Number(raw);

    if (Number.isFinite(next)) {
      onChange(next);
    }
  };

  return (
    <div class="ui-control ui-control-wide">
      <span class="ui-control-label">{label}</span>
      <input
        ref={input}
        type="number"
        class="ui-number-input"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        step={step ?? 1}
        onInput={(event) => {
          // The spinner is a finished edit; a keystroke is not (4 is not 4096 yet).
          const inputType = 'inputType' in event ? String(event.inputType) : '';

          if (inputType === 'stepUp' || inputType === 'stepDown') {
            commit(event.currentTarget.value);
          }
        }}
        onBlur={(event) => commit(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}

/**
 * Two bounds edited as one field. An environment range is rolled per race, so authoring it means
 * setting how far a value may wander; equal bounds are how you say "always exactly this".
 */
export function RangeField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: { min: number; max: number };
  min?: number;
  max?: number;
  step?: number;
  onChange: (next: { min: number; max: number }) => void;
}): JSX.Element {
  return (
    <div
      class="ui-control ui-control-wide ui-range"
      role="group"
      aria-label={label}
    >
      <span class="ui-control-label">{label}</span>
      <div class="ui-range-inputs">
        <NumberField
          label={`${label} min`}
          value={value.min}
          min={min}
          max={max}
          step={step}
          onChange={(next) => onChange({ ...value, min: next })}
        />
        <NumberField
          label={`${label} max`}
          value={value.max}
          min={min}
          max={max}
          step={step}
          onChange={(next) => onChange({ ...value, max: next })}
        />
      </div>
    </div>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (next: number) => void;
  hint?: string;
}): JSX.Element {
  return (
    <div class="ui-control ui-control-wide ui-slider" title={hint}>
      <span class="ui-control-label">{label}</span>
      <div class="ui-slider-body">
        <input
          type="range"
          class="ui-slider-input"
          aria-label={label}
          aria-description={hint}
          min={min}
          max={max}
          step={step}
          value={value}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={display}
          title={hint}
          onInput={(event) => onChange(Number(event.currentTarget.value))}
        />
        <span class="ui-slider-value" aria-live="polite">
          {display}
        </span>
      </div>
    </div>
  );
}

/**
 * Live value on `input`; finished edit on `change` / pointer-up. Left uncontrolled while focused
 * so a parent re-render cannot snap the thumb back mid-drag.
 */
export function LiveSlider({
  label,
  value,
  minLabel,
  maxLabel,
  dataAttr,
  hint,
  onInput,
  onCommit,
}: {
  label: string;
  value: number;
  minLabel?: string;
  maxLabel?: string;
  dataAttr?: string;
  hint?: string;
  onInput: (next: number) => void;
  onCommit: () => void;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const element = input.current;

    if (element && document.activeElement !== element) {
      element.value = String(value);
    }
  }, [value]);

  const dataProps = dataAttr ? { [dataAttr]: '' } : {};

  return (
    <div class="ui-control ui-control-wide ui-slider ui-slider-live" title={hint}>
      <span class="ui-control-label">{label}</span>
      <div
        class={
          minLabel || maxLabel
            ? 'ui-slider-body ui-slider-body-ends'
            : 'ui-slider-body'
        }
      >
        {minLabel ? <span class="ui-slider-end">{minLabel}</span> : null}
        <input
          ref={input}
          type="range"
          class="ui-slider-input"
          aria-label={label}
          min={0}
          max={1}
          step={0.01}
          defaultValue={String(value)}
          {...dataProps}
          onInput={(event) => {
            const next = Number(event.currentTarget.value);

            if (Number.isFinite(next)) {
              onInput(next);
            }
          }}
          onChange={() => onCommit()}
          onPointerUp={() => onCommit()}
        />
        {maxLabel ? <span class="ui-slider-end">{maxLabel}</span> : null}
      </div>
    </div>
  );
}

export function Stepper({
  label,
  value,
  disabled = false,
  onStep,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onStep: (direction: -1 | 1) => void;
}): JSX.Element {
  return (
    <div class="ui-control ui-control-stepper">
      <span class="ui-control-label">{label}</span>
      <span class="ui-stepper-value" aria-live="polite">
        {value}
      </span>
      <div class="ui-stepper-buttons">
        <button
          type="button"
          class="ui-button ui-step"
          aria-label={`Decrease ${label.toLowerCase()}`}
          disabled={disabled}
          onClick={() => onStep(-1)}
        >
          −
        </button>
        <button
          type="button"
          class="ui-button ui-step"
          aria-label={`Increase ${label.toLowerCase()}`}
          disabled={disabled}
          onClick={() => onStep(1)}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * A toggle button with an inline, typeable stepper on the same row.
 * Renders in the four-column `.ui-control` grid: label | toggle | value field | +/- buttons.
 *
 * The field follows the `NumberField` contract — uncontrolled while focused so a re-render cannot
 * overwrite a half-typed number, committing on blur or Enter. Escape abandons the edit. There is
 * no ceiling; the owner clamps whatever is committed.
 */
export function ToggleStepper({
  label,
  toggleValue,
  onToggle,
  value,
  unit,
  onStep,
  onValueCommit,
}: {
  label: string;
  toggleValue: boolean;
  onToggle: (next: boolean) => void;
  value: number;
  unit: string;
  onStep: (direction: -1 | 1) => void;
  onValueCommit: (next: number) => void;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const element = input.current;

    if (element && document.activeElement !== element) {
      element.value = String(value);
    }
  }, [value]);

  const restore = (): void => {
    if (input.current) {
      input.current.value = String(value);
    }
  };

  const commit = (raw: string): void => {
    const next = Number(raw);

    if (raw.trim() === '' || !Number.isFinite(next)) {
      restore();
      return;
    }

    onValueCommit(next);
  };

  return (
    <div class="ui-control">
      <span class="ui-control-label">{label}</span>
      <button
        type="button"
        class="ui-button ui-toggle"
        aria-pressed={toggleValue ? 'true' : 'false'}
        aria-label={label}
        onClick={() => onToggle(!toggleValue)}
      >
        {toggleValue ? 'on' : 'off'}
      </button>
      <span class="ui-stepper-field">
        <input
          ref={input}
          type="text"
          inputMode="numeric"
          class="ui-stepper-input"
          aria-label={`${label} halo`}
          disabled={!toggleValue}
          onBlur={(event) => commit(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            } else if (event.key === 'Escape') {
              restore();
              event.currentTarget.blur();
            }
          }}
        />
        <span class="ui-stepper-unit">{unit}</span>
      </span>
      <div class="ui-stepper-buttons">
        <button
          type="button"
          class="ui-button ui-step"
          aria-label={`Decrease ${label.toLowerCase()} halo`}
          disabled={!toggleValue}
          onClick={() => onStep(-1)}
        >
          −
        </button>
        <button
          type="button"
          class="ui-button ui-step"
          aria-label={`Increase ${label.toLowerCase()} halo`}
          disabled={!toggleValue}
          onClick={() => onStep(1)}
        >
          +
        </button>
      </div>
    </div>
  );
}
